import { Box, Chip, LinearProgress, Paper, Tooltip } from "@mui/material";
import { Launch } from "@mui/icons-material";
import { alpha, useTheme } from "@mui/system";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import DatagridComponent from "../../../../components/datagridComponent";
import useStyles from "../styles";
import ClienteComTipo from "../components/clienteComTipo";

/**
 * Coluna esquerda da aba CPE: tabela de clientes.
 * A barra de abas CPE/ONU e o painel direito (Atendimentos/Tráfego) vivem no
 * index.js. A linha selecionada é elevada via `setSelectedRow`.
 */
export default function TabCPE({
	filteredRows,
	loading,
	initial_date,
	formatUptime,
	setSelectedRow,
}) {
	const theme = useTheme();
	const styles = useStyles(theme);
	const navigate = useNavigate();

	const handleLinkClick = (e, params) => {
		e.stopPropagation();

		const clientStorage = {
			interfaceId: null,
			clienteId: params?.row?.clienteId,
			hostId: params?.row?.hostId,
			device: params?.row?.device,
			selectTime: 5,
			initialDate: null,
			finalDate: null,
			serviceId: params?.row?.servicoId,
			fabricante: params?.row?.fabricante ?? null,
			funcao: params?.row?.funcao ?? null,
			modelo: params?.row?.modelo ?? null,
		};

		localStorage.setItem("filterData", JSON.stringify(clientStorage));

		if (e.button === 1 || e.ctrlKey || e.metaKey) {
			window.open(`/interno/dash/overview`, "_blank");
		} else {
			navigate(`/interno/dash/overview`);
		}
	};

	const linkView = (params) => {
		return (
			<Box sx={styles.launchContainer}>
				<Tooltip title={"Navegar até a overview"}>
					<Launch
						size="small"
						sx={styles.launchIcon}
						onClick={(e) => handleLinkClick(e, params)}
						onAuxClick={(e) => {
							if (e.button === 1) {
								handleLinkClick(e, params);
							}
						}}
					/>
				</Tooltip>
			</Box>
		);
	};

	const columns = [
		{ editable: false, field: "id", headerName: "ID", hide: true },
		{
			field: "nome",
			headerName: "Cliente",
			flex: 1,
			renderCell: (params) => (
				<ClienteComTipo
					nome={params?.row?.nome || ""}
					tipoCliente={params?.row?.tipoCliente}
				/>
			),
		},
		{ field: "host", headerName: "Host", flex: 1 },
		{
			field: "consume",
			headerName: "95Th",
			flex: 0.6,
			type: "number",
			valueGetter: (value, row) => {
				const res = row?.percentile?.find((th) =>
					dayjs(initial_date)?.isSame(dayjs(th?.refferenceMonth), "month"),
				);
				return !!res ? Number(res?.percentilConsumption) : null;
			},
			valueFormatter: (value) => (value != null ? formatBytes(value) : ""),
		},
		{
			field: "uptime",
			headerName: "Uptime",
			flex: 0.7,
			valueGetter: (value, row) => formatUptime(row?.uptime),
		},
		{
			field: "availability",
			headerName: "Disponibilidade",
			flex: 0.7,
			renderCell: (params) => (
				<Chip
					label={params?.value ? "up" : "down"}
					sx={{
						backgroundColor: params?.value
							? theme.palette.chip.background.success
							: theme.palette.chip.background.critical,
						color: params?.value
							? theme.palette.chip.colorText.success
							: theme.palette.chip.colorText.critical,
						border: "0.6px solid ",
						borderColor: params?.value
							? theme.palette.chip.colorText.success
							: theme.palette.chip.colorText.critical,
						borderRadius: "10px",
						height: "20px",
						width: "52px",
					}}
					size="small"
				/>
			),
			valueGetter: (value, row) => parseInt(row?.availability),
		},
		{
			field: "link",
			headerName: "",
			sortable: false,
			width: 0.1,
			renderCell: linkView,
		},
	];

	return (
		<Paper
			sx={{
				...styles.paperClientes,
				borderTopLeftRadius: 0,
				position: "relative",
			}}
			variant="outlined"
			elevation={theme.palette.mode == "dark" ? 0 : 3}
		>
			{loading && <LinearProgress sx={styles.linearProgress} />}
			<DatagridComponent
				columns={columns}
				rows={filteredRows || []}
				setSelectedRow={setSelectedRow}
				isPagination={false}
				sxDatagrid={{
					height: "100%",
					borderRadius: "10px",
					"& .MuiDataGrid-row.Mui-selected": {
						backgroundImage: theme.palette.gradient.buttonSelected,
						"& .MuiDataGrid-cell": {
							color: theme.palette.white.main,
						},
					},
					"& .MuiDataGrid-row:hover": {
						backgroundImage: `linear-gradient(
                                    90deg,
                                    ${alpha(theme.palette.buttonHoverGradient.dark, 0.5)} 0%,
                                    ${alpha(theme.palette.buttonHoverGradient.light, 0.5)} 100%
                                )`,
					},
				}}
				options={{ disableRowSelectionOnClick: false }}
			/>
		</Paper>
	);
}

const formatBytes = (bytes) => {
	if (bytes == 0) return "0 B";
	const k = 1000;
	const sizes = ["bps", "Kbps", "Mbps", "Gbps", "Tbps", "Pbps", "Ebps"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};
